/**
 * TEMPORARY in-app feedback tool — the floating button + note modal.
 *
 * Self-test logger: a 🐞 button (mid-right, clear of the HUD and the chip stack)
 * grabs a screenshot of the current screen, then opens a modal that shows the
 * auto-captured context, takes a free-text note + optional area tag, and saves it
 * (localStorage + POST to /__feedback, which writes the jsonl and the PNG). "Copy
 * all" / "Download" dump every note, and "Download screenshots" rescues the
 * session's images, so nothing is lost if the endpoint is down.
 *
 * BOLT-ON: to strip for release, delete the src/feedback folder, remove the one
 * <FeedbackButton/> import+render in Shell.tsx, and the feedback plugin line in
 * vite.config.ts. Or flip FEEDBACK_ENABLED to false.
 */
import { useState, useEffect, type CSSProperties } from 'react';
import { captureContext, makeEntry, type FeedbackContext } from './captureContext';
import { captureScreenshot, type Screenshot } from './captureScreenshot';
import { useFeedbackStore, loadDraft, persistDraft, clearDraft } from './feedbackStore';

/** Single switch to disable the whole tool without removing it. */
export const FEEDBACK_ENABLED = true;

const Z = 9999;

function activeOverlays(c: FeedbackContext): string {
  const on = Object.entries(c.overlays)
    .filter(([, v]) => v)
    .map(([k]) => k);
  return on.length ? on.join(', ') : 'none';
}

function ContextRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 12, lineHeight: 1.5 }}>
      <span style={{ flex: '0 0 86px', color: '#3a4a66' }}>{label}</span>
      <span style={{ flex: 1, color: '#163a70', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
}

function fmtPos(p: [number, number] | null): string {
  return p ? `${p[1].toFixed(4)}, ${p[0].toFixed(4)}` : '—';
}

export function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [area, setArea] = useState('');
  const [ctx, setCtx] = useState<FeedbackContext | null>(null);
  const [shot, setShot] = useState<Screenshot | null>(null);
  const entries = useFeedbackStore((s) => s.entries);
  const images = useFeedbackStore((s) => s.images);
  const syncing = useFeedbackStore((s) => s.syncing);
  const syncedIds = useFeedbackStore((s) => s.syncedIds);
  const flush = useFeedbackStore((s) => s.flush);
  const resendAll = useFeedbackStore((s) => s.resendAll);
  const add = useFeedbackStore((s) => s.add);
  const clearAll = useFeedbackStore((s) => s.clear);
  const exportJsonl = useFeedbackStore((s) => s.exportJsonl);
  const downloadImages = useFeedbackStore((s) => s.downloadImages);

  // Retry any unsynced reports whenever the panel opens (a natural moment, and
  // covers the case where earlier syncs failed while offline).
  useEffect(() => {
    if (open) void flush();
  }, [open, flush]);

  // Persist the in-progress draft as it is typed, debounced, while the modal is
  // open, so a crash loses at most the last few hundred ms rather than the note.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => persistDraft({ text, area }), 400);
    return () => clearTimeout(t);
  }, [open, text, area]);

  if (!FEEDBACK_ENABLED) return null;

  const imageCount = Object.keys(images).length;

  // Grab the screenshot BEFORE the modal opens, so the picture shows the screen
  // the user was looking at, not the dialog sitting on top of it. Also bring back
  // any half-typed draft so a crash or accidental close mid-report never loses
  // the words — the box reopens exactly where it was left.
  async function openModal() {
    setShot(null);
    const draft = loadDraft();
    setText(draft.text);
    setArea(draft.area);
    const s = await captureScreenshot();
    setShot(s);
    // Context AFTER the shot, deliberately: the context carries the capture
    // diagnostics (which read path produced the chart pixels), and reading it
    // first would attach the PREVIOUS shot's diagnostics to this report.
    setCtx(captureContext());
    setOpen(true);
  }

  // Closing without saving keeps the draft: flush it now (covers the case of a
  // close within the debounce window), so reopening restores it.
  function closeModal() {
    persistDraft({ text, area });
    setOpen(false);
  }

  function save() {
    if (!ctx || !text.trim()) return;
    const entry = makeEntry(text, area, ctx);
    // The endpoint writes the PNG to this deterministic path; record it now so the
    // saved entry points at the image even before the POST comes back.
    if (shot) entry.screenshot = `screenshots/${entry.id}.png`;
    add(entry, shot?.dataUrl);
    // The report is now in the durable queue, so the draft has done its job.
    clearDraft();
    setText('');
    setArea('');
    setOpen(false);
  }

  async function copyAll() {
    const data = exportJsonl();
    try {
      await navigator.clipboard.writeText(data);
    } catch {
      /* clipboard blocked — the Download button is the fallback */
    }
  }

  function download() {
    const blob = new Blob([exportJsonl()], { type: 'application/x-ndjson' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'feedback-log.jsonl';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const btnStyle: CSSProperties = {
    position: 'absolute',
    right: 12,
    top: '46%',
    zIndex: Z,
    pointerEvents: 'auto',
    width: 46,
    height: 46,
    borderRadius: 14,
    border: '1px solid rgba(255,255,255,0.55)',
    background: 'rgba(224, 120, 0, 0.92)',
    color: '#fff',
    fontSize: 20,
    cursor: 'pointer',
    boxShadow: '0 6px 18px rgba(6,14,32,0.3)',
  };

  return (
    <>
      <button
        type="button"
        style={btnStyle}
        aria-label="Log feedback about what's on screen"
        title="Feedback (temporary self-test logger)"
        onClick={() => void openModal()}
      >
        🐞
        {/* No saved-notes count badge here. It sits mid-right, so with the map
            centred on the vessel it reads as a bare "+6" style label pinned to
            the boat and gets mistaken for a navigation value (fb-ms390m7m-1). The
            count is still shown in the feedback panel ("N notes saved"), which is
            where it belongs. */}
      </button>

      {open && ctx && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Feedback"
          onClick={() => closeModal()}
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: Z + 1,
            pointerEvents: 'auto',
            background: 'rgba(15,28,36,0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(420px, 100%)',
              maxHeight: '86%',
              overflowY: 'auto',
              background: '#f7fbff',
              borderRadius: 16,
              border: '1px solid rgba(255,255,255,0.8)',
              boxShadow: '0 16px 40px rgba(6,14,32,0.35)',
              padding: 16,
              color: '#163a70',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <b style={{ fontSize: 15 }}>Feedback · what’s not right?</b>
              <button
                type="button"
                onClick={() => closeModal()}
                aria-label="Close"
                style={{ width: 28, height: 28, borderRadius: 9, border: '1px solid #d7deef', background: '#dfe3fb', color: '#243a8f', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            <div style={{ background: '#eef2fb', borderRadius: 10, padding: '8px 10px', marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#243a8f', marginBottom: 4 }}>
                Captured context
              </div>
              <ContextRow label="Time" value={ctx.at} />
              <ContextRow label="Mode" value={`${ctx.shellMode} · ${ctx.worldMode}`} />
              <ContextRow label="Doing" value={ctx.activity} />
              <ContextRow label="Open" value={ctx.openSheet ?? 'chart (no sheet)'} />
              {ctx.menuLocation && <ContextRow label="Menu" value={ctx.menuLocation} />}
              {ctx.activeTool && ctx.activeTool !== 'pan' && ctx.activeTool !== 'none' && (
                <ContextRow label="Tool" value={ctx.activeTool} />
              )}
              {ctx.selectedObject && <ContextRow label="Object" value={ctx.selectedObject} />}
              <ContextRow label="Overlays" value={activeOverlays(ctx)} />
              <ContextRow label="Chart" value={`${ctx.chartProvider} · ${ctx.symbolSet} · ${ctx.palette}`} />
              <ContextRow label="Centre" value={fmtPos(ctx.centre)} />
              <ContextRow label="Zoom~" value={ctx.zoomApprox == null ? '—' : String(ctx.zoomApprox)} />
              <ContextRow label="Own ship" value={fmtPos(ctx.ownship)} />
            </div>

            {shot ? (
              <div style={{ marginBottom: 10 }}>
                <img
                  src={shot.dataUrl}
                  alt="Screen capture"
                  style={{ width: '100%', borderRadius: 10, border: '1px solid #d7deef', display: 'block' }}
                />
                <div style={{ fontSize: 11, color: '#3a4a66', marginTop: 4 }}>
                  {shot.method === 'html2canvas' ? 'Full screen captured' : 'Chart only, overlays not captured'}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: '#3a4a66', marginBottom: 10 }}>No screenshot captured</div>
            )}

            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="What's not right here?"
              rows={4}
              autoFocus
              style={{ width: '100%', borderRadius: 10, border: '1px solid #d7deef', padding: 9, fontSize: 14, fontFamily: 'inherit', resize: 'vertical', color: '#163a70', background: '#fff', WebkitTextFillColor: '#163a70', colorScheme: 'light' }}
            />
            <input
              value={area}
              onChange={(e) => setArea(e.target.value)}
              placeholder="Area / tag (optional, e.g. range rings)"
              style={{ width: '100%', marginTop: 8, borderRadius: 10, border: '1px solid #d7deef', padding: '8px 9px', fontSize: 13, fontFamily: 'inherit', color: '#163a70', background: '#fff', WebkitTextFillColor: '#163a70', colorScheme: 'light' }}
            />

            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button
                type="button"
                onClick={save}
                disabled={!text.trim()}
                style={{ flex: 1, padding: '10px 0', borderRadius: 11, border: 'none', background: text.trim() ? '#5e72eb' : '#c7cdf2', color: '#fff', fontWeight: 700, fontSize: 14, cursor: text.trim() ? 'pointer' : 'default' }}
              >
                Save
              </button>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
              <button type="button" onClick={copyAll} disabled={entries.length === 0} style={smallBtn(entries.length === 0)}>
                Copy all
              </button>
              <button type="button" onClick={download} disabled={entries.length === 0} style={smallBtn(entries.length === 0)}>
                Download
              </button>
              <button
                type="button"
                onClick={() => void resendAll()}
                disabled={entries.length === 0 || syncing}
                style={smallBtn(entries.length === 0 || syncing)}
                title="Re-push every note to the collector (safe: it de-dupes by id)"
              >
                {syncing ? 'Sending…' : 'Resend all'}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (entries.length && window.confirm('Delete all saved feedback notes?')) clearAll();
                }}
                disabled={entries.length === 0}
                style={smallBtn(entries.length === 0)}
              >
                Clear all
              </button>
            </div>

            {imageCount > 0 && (
              <div style={{ display: 'flex', marginTop: 8 }}>
                <button type="button" onClick={downloadImages} style={smallBtn(false)}>
                  Download {imageCount} screenshot{imageCount === 1 ? '' : 's'}
                </button>
              </div>
            )}

            <div style={{ marginTop: 8, fontSize: 11, color: '#3a4a66' }}>
              {(() => {
                const synced = entries.filter((e) => syncedIds.includes(e.id)).length;
                const unsynced = entries.length - synced;
                return (
                  <>
                    {entries.length} note{entries.length === 1 ? '' : 's'} saved · {synced} synced
                    {unsynced > 0 ? ` · ${unsynced} waiting to sync` : ' ✓'}
                    {unsynced > 0 ? ' (kept safe locally — Copy all / Download always works)' : ''}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function smallBtn(disabled: boolean): CSSProperties {
  return {
    flex: 1,
    padding: '8px 0',
    borderRadius: 10,
    border: '1px solid #d7deef',
    background: '#fff',
    color: disabled ? '#aab2c8' : '#163a70',
    fontSize: 12,
    fontWeight: 600,
    cursor: disabled ? 'default' : 'pointer',
  };
}
